import { z } from 'zod';
import { AVATAR_ASSET_PATHS, AVATAR_MOUTH_LIPSTICK_ASSET_PATHS } from '../assets/avatars/humanAvatarAssetUrls';
import { createRequiredRecord } from '../utils/zodUtils';
export const AVATAR_TYPE_LABELS = {
    unique: 'Unique Avatar',
    legacy: 'Avatar',
    custom: 'Avatar'
};
export const ALL_AVATAR_TYPE_PROPERTIES = {
    custom: {
        canEdit: true,
        canCreateCustomAvatar: true
    },
    unique: {
        canEdit: false,
        canCreateCustomAvatar: false
    },
    legacy: {
        canEdit: false,
        canCreateCustomAvatar: true
    }
};
export const ALL_HUMAN_AVATAR_PART_TYPES = [
    'head',
    'hair',
    'eyes',
    'mouth',
    'ears',
    'eyebrows',
    'nose',
    'eyewear',
    'facialLines',
    'facialHair',
    'backHair'
];
const HumanAvatarPartTypeSchema = z.enum(ALL_HUMAN_AVATAR_PART_TYPES);
export const ALL_HUMAN_AVATAR_COLOR_TYPES = ['skin', 'hair'];
const HumanAvatarColorTypeSchema = z.enum(ALL_HUMAN_AVATAR_COLOR_TYPES);
export const HUMAN_AVATAR_COLOR_TYPES = {
    skin: {
        colorType: 'skin',
        label: 'Skin Color'
    },
    hair: {
        colorType: 'hair',
        label: 'Hair Color'
    }
};
export const HUMAN_AVATAR_PART_DEFINITIONS = {
    head: {
        partType: 'head',
        label: 'Head',
        imageUrls: AVATAR_ASSET_PATHS.head,
        browseImageScale: 0.3
    },
    hair: {
        partType: 'hair',
        label: 'Hair',
        imageUrls: AVATAR_ASSET_PATHS.hair,
        optionalProperties: { randomChanceOfNone: 0.2 },
        browseImageScale: 0.3
    },
    eyes: {
        partType: 'eyes',
        label: 'Eyes',
        imageUrls: AVATAR_ASSET_PATHS.eyes
    },
    mouth: {
        partType: 'mouth',
        label: 'Mouth',
        imageUrls: AVATAR_ASSET_PATHS.mouth,
        optionalProperties: { randomChanceOfNone: 0.05 },
        subTypesDefinition: {
            defaultSubTypeLabel: 'Plain',
            additionalSubTypes: [{ type: 'lipstick', label: 'Lipstick', imageUrls: AVATAR_MOUTH_LIPSTICK_ASSET_PATHS }]
        }
    },
    facialHair: {
        partType: 'facialHair',
        label: 'Facial Hair',
        imageUrls: AVATAR_ASSET_PATHS.facialHair,
        optionalProperties: { randomChanceOfNone: 0.7 }
    },
    eyewear: {
        partType: 'eyewear',
        label: 'Eyewear',
        imageUrls: AVATAR_ASSET_PATHS.eyewear,
        optionalProperties: { randomChanceOfNone: 0.7 }
    },
    ears: {
        partType: 'ears',
        label: 'Ears',
        imageUrls: AVATAR_ASSET_PATHS.ears,
        optionalProperties: { randomChanceOfNone: 0.1 }
    },
    eyebrows: {
        partType: 'eyebrows',
        label: 'Eyebrows',
        imageUrls: AVATAR_ASSET_PATHS.eyebrows,
        optionalProperties: { randomChanceOfNone: 0.2 }
    },
    nose: {
        partType: 'nose',
        label: 'Nose',
        imageUrls: AVATAR_ASSET_PATHS.nose,
        optionalProperties: { randomChanceOfNone: 0.1 }
    },
    facialLines: {
        partType: 'facialLines',
        label: 'Facial Lines',
        imageUrls: AVATAR_ASSET_PATHS.facialLines,
        optionalProperties: { randomChanceOfNone: 0.5 },
        browseImageScale: 0.3
    },
    backHair: {
        partType: 'backHair',
        isDisabled: true,
        label: 'Back Hair',
        imageUrls: AVATAR_ASSET_PATHS.backHair,
        optionalProperties: { randomChanceOfNone: 0.1 },
        browseImageScale: 0.3
    }
};
const HumanAvatarPartSchema = z.object({
    index: z.number(), // -1 means "off"
    subType: z.optional(z.string())
});
const HumanAvatarPropertiesSchema = z.object({
    colors: createRequiredRecord(HumanAvatarColorTypeSchema, z.string()),
    parts: createRequiredRecord(HumanAvatarPartTypeSchema, HumanAvatarPartSchema)
});
const HumanAvatarPropertiesSetSchema = z.object({
    set: z.literal('human'),
    properties: HumanAvatarPropertiesSchema
});
export const AvatarPropertiesSetSchema = HumanAvatarPropertiesSetSchema;
const DEFAULT_SKIN_COLOR = '#6C757D';
const DEFAULT_HAIR_COLOR = '#343A40';
export const DEFAULT_AVATAR_PROPERTIES_SET = {
    set: 'human',
    properties: {
        colors: { skin: DEFAULT_SKIN_COLOR, hair: DEFAULT_HAIR_COLOR },
        parts: {
            head: { index: 0 },
            hair: { index: -1 },
            eyes: { index: 0 },
            mouth: { index: 0 },
            ears: { index: -1 },
            nose: { index: 0 },
            eyebrows: { index: 0 },
            eyewear: { index: -1 },
            facialLines: { index: -1 },
            facialHair: { index: -1 },
            backHair: { index: -1 }
        }
    }
};
